import { FileType } from "components/FileInput/FileInput";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { UploadFromSelectorModal } from "components/UploadFromSelectorModal/UploadFromSelectorModal";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useCreateChatQNAMutation } from "store/apis/chatQnA";
import { useChannelStateContext } from "stream-chat-react";
import { SelecteFile } from "types/Models/documents";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { pluralize } from "utils/pluralize";
import { RequestComposeView } from "./RequestComposeView";
import { RequestDocumentUpload } from "./RequestDocumentUpload";

export type StagedDocument = {
  id: string;
  file: File | SelecteFile;
  name: string;
  previewUrl: string;
  fileType: string;
};

export const filesToStagedDocs = (files: FileType[]): StagedDocument[] =>
  files.map((file) => {
    if (file instanceof File) {
      let fileType = "OTHER";
      if (file.type === "application/pdf") fileType = "PDF";
      if (file.type.startsWith("image/")) fileType = "IMAGE";
      return {
        id: crypto.randomUUID(),
        file,
        name: file.name,
        previewUrl: URL.createObjectURL(file),
        fileType,
      };
    }
    const selecteFile = file as SelecteFile;
    return {
      id: selecteFile.uuid,
      file: selecteFile,
      name: selecteFile.name,
      previewUrl: "",
      fileType: selecteFile.file_type,
    };
  });

export type DraftQuestion = {
  id: string;
  text: string;
  docIndex: number | null;
  x: number | null;
  y: number | null;
  pageNumber: number | null;
};

export const createEmptyQuestion = (): DraftQuestion => ({
  id: crypto.randomUUID(),
  text: "",
  docIndex: null,
  x: null,
  y: null,
  pageNumber: null,
});

type ChatRequestStep = "upload" | "compose";

export const ChatRequestAnswer = ({ isOpen, close }: ModalProps) => {
  const [step, setStep] = useState<ChatRequestStep>("upload");
  const [stagedDocs, setStagedDocs] = useState<StagedDocument[]>([]);
  const [questions, setQuestions] = useState<DraftQuestion[]>([]);
  const [hasAttemptedSend, setHasAttemptedSend] = useState(false);
  const [scrollToQuestionId, setScrollToQuestionId] = useState<string | null>(
    null
  );

  const { alertToast, successToast } = useToast();
  const [createQuestion, { isLoading: isCreating }] =
    useCreateChatQNAMutation();
  const { channel } = useChannelStateContext();
  const groupId = channel?.data?.group_uuid as string;
  const uploadModal = useModal();

  const handleNext = () => {
    if (questions.length === 0) setQuestions([createEmptyQuestion()]);
    setStep("compose");
  };

  const handleSend = async () => {
    setHasAttemptedSend(true);
    const firstEmptyQuestion = questions.find((q) => !q.text.trim());
    if (firstEmptyQuestion) {
      setScrollToQuestionId(firstEmptyQuestion.id);
      return;
    }
    setScrollToQuestionId(null);

    try {
      if (!channel?.id || !groupId) {
        throw new Error("Channel or group ID is not available.");
      }

      const referenceDocs = stagedDocs.map((doc, index) =>
        doc.file instanceof File
          ? { file: doc.file, docIndex: index }
          : { fileId: (doc.file as SelecteFile).uuid, docIndex: index }
      );

      await createQuestion({
        groupId,
        channelId: channel.id,
        requestData: questions.map((q, index) => ({
          question: q.text,
          sequence: index + 1,
          ...(q.docIndex != null && { documentIndex: q.docIndex }),
          ...(q.x != null && { xPosition: q.x }),
          ...(q.y != null && { yPosition: q.y }),
          ...(q.pageNumber != null && { pageNumber: q.pageNumber }),
        })),
        referenceDocs,
      }).unwrap();

      successToast({ message: "Information requested" });
      close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleClose = () => {
    setStep("upload");
    setStagedDocs([]);
    setQuestions([]);
    setHasAttemptedSend(false);
    close();
  };

  const handleQuestionsChange = (updatedQuestions: DraftQuestion[]) => {
    setQuestions(updatedQuestions);
  };

  const errorQuestionIds = hasAttemptedSend
    ? questions.filter((q) => !q.text.trim()).map((q) => q.id)
    : [];

  const handleUploadMoreDocs = (files: FileType[]) => {
    setStagedDocs([...stagedDocs, ...filesToStagedDocs(files)]);
  };

  const hasQuestions = questions.length > 0;
  const hasStagedDocs = stagedDocs.length > 0;

  const composeModalSize = hasStagedDocs ? "xxxl" : "xxl";
  const modalSize = step === "upload" ? "regular" : composeModalSize;

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose}>
      <Modal.Content
        size={modalSize}
        className={
          step === "compose" && hasStagedDocs ? "t-h-[90vh]" : undefined
        }
      >
        <Modal.Header>
          <Modal.Title>Request Information</Modal.Title>
          <Modal.Close type="button" />
        </Modal.Header>

        {step === "upload" ? (
          <Modal.Body>
            <div className="t-flex t-flex-col t-gap-4">
              <p className="t-text-body t-text-text-30 t-m-0">
                Add documents if your questions are about something inside one.
                You can add more later.
              </p>
              <RequestDocumentUpload
                docs={stagedDocs}
                onDocsChange={setStagedDocs}
                groupId={groupId}
              />
            </div>
          </Modal.Body>
        ) : (
          <Modal.Body asChild>
            <RequestComposeView
              stagedDocs={stagedDocs}
              onStagedDocsChange={setStagedDocs}
              questions={questions}
              onQuestionsChange={handleQuestionsChange}
              groupId={groupId}
              errorQuestionIds={errorQuestionIds}
              scrollToQuestionId={scrollToQuestionId}
            />
          </Modal.Body>
        )}

        <Modal.Footer>
          {step === "upload" ? (
            <div className="t-flex t-justify-end t-gap-3 t-w-full">
              <Modal.RawClose asChild>
                <Button type="button">Cancel</Button>
              </Modal.RawClose>
              <Button type="button" customType="primary" onClick={handleNext}>
                {stagedDocs.length > 0
                  ? `Continue with ${pluralize(
                      stagedDocs.length,
                      "document",
                      "documents"
                    )}`
                  : "Continue without documents"}
              </Button>
            </div>
          ) : (
            <div className="t-flex t-justify-between t-items-center t-gap-3 t-w-full">
              {hasStagedDocs ? (
                <div />
              ) : (
                <Button
                  size="small"
                  customType="link"
                  onClick={uploadModal.open}
                >
                  + Add document
                </Button>
              )}

              <div className="t-flex t-gap-3">
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  type="button"
                  customType="primary"
                  onClick={handleSend}
                  disabled={!hasQuestions || isCreating}
                  isLoading={isCreating}
                >
                  Send to customer
                </Button>
              </div>
            </div>
          )}
        </Modal.Footer>
      </Modal.Content>

      {uploadModal.isOpen && (
        <UploadFromSelectorModal
          isOpen={uploadModal.isOpen}
          close={uploadModal.close}
          onDrop={handleUploadMoreDocs}
          groupId={groupId}
          allowSingleUpload={false}
        />
      )}
    </Modal.Root>
  );
};
